import { Check, ChevronDown } from "lucide-react";
import { cn } from "~/lib/utils";
import React from "react";
import { Tooltip } from "~/components/Tooltip";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLock } from "@fortawesome/pro-solid-svg-icons";

// // // //

function PeriodSelectLoginGating(props: {
    isLoggedIn: boolean;
    children: React.ReactNode;
}) {
    const { isLoggedIn, children } = props;

    if (isLoggedIn) {
        return <>{children}</>;
    }

    return (
        <Tooltip
            placement="top"
            tooltipContent={
                <div className="flex items-center gap-1.5 px-3 py-2 text-sm">
                    <FontAwesomeIcon icon={faLock} className="h-2.5 w-2.5" />
                    <p className="text-sm">Log in to unlock controls</p>
                </div>
            }
        >
            {({ setTriggerRef }) => <div ref={setTriggerRef}>{children}</div>}
        </Tooltip>
    );
}

// // // //

interface PeriodOption {
    value: string;
    label: string;
    testID: string;
}

interface PeriodSelectProps {
    options?: PeriodOption[];
    value?: string;
    defaultValue?: string;
    onChange?: (value: string) => void;
    className?: string;
    disabled?: boolean;
    size?: "sm" | "md";
}

function PeriodSelect({
    options = [
        { value: "7D", label: "7D", testID: "timeframe-select--7d" },
        { value: "30D", label: "30D", testID: "timeframe-select--30d" },
        { value: "90D", label: "90D", testID: "timeframe-select--90d" },
        { value: "6M", label: "6M", testID: "timeframe-select--6m" },
        { value: "1Y", label: "1Y", testID: "timeframe-select--1y" },
        { value: "YTD", label: "YTD", testID: "timeframe-select--ytd" },
    ],
    value,
    defaultValue = "30D",
    size = "md",
    onChange,
    className,
    disabled = false,
}: PeriodSelectProps) {
    // Use internal state only if component is uncontrolled
    const [internalValue, setInternalValue] = React.useState(defaultValue);
    const [open, setOpen] = React.useState(false);
    const ref = React.useRef<HTMLDivElement>(null);

    // Determine if component is controlled
    const isControlled = value !== undefined;
    // Use the value prop if controlled, otherwise use internal state
    const currentValue = isControlled ? value : internalValue;
    const currentLabel = options.find(
        (option) => option.value === currentValue,
    )?.label;

    const handleSelect = (optionValue: string) => {
        if (disabled) return;

        // Only update internal state if uncontrolled
        if (!isControlled) {
            setInternalValue(optionValue);
        }
        // Always call onChange if provided
        onChange?.(optionValue);
        setOpen(false);
    };

    // Close dropdown when clicking outside
    React.useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (ref.current && !ref.current.contains(event.target as Node)) {
                setOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    return (
        <div ref={ref} className={cn("relative inline-block", className)}>
            {/* Trigger button */}
            <button
                type="button"
                onClick={() => !disabled && setOpen(!open)}
                data-testid="timeframe-select"
                className={cn(
                    "flex items-center justify-between w-full px-3 bg-card border rounded-md font-medium text-foreground transition-colors",
                    !disabled &&
                        "hover:bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-primary/20",
                    disabled && "opacity-50 cursor-not-allowed",
                    {
                        "py-0.5 text-xs": size === "sm",
                        "py-1.5 text-sm": size === "md",
                    },
                )}
            >
                <div className="flex items-center justify-between w-full">
                    <div className="flex items-center">
                        <span className="text-foreground mr-1">Period:</span>
                        <span className="text-foreground whitespace-nowrap">
                            {currentLabel}
                        </span>
                    </div>
                    <ChevronDown className="h-5 w-5 min-w-5 text-secondary-foreground" />
                </div>
            </button>

            {/* Dropdown */}
            {open && (
                <div className="absolute z-10 mt-1 w-full bg-card rounded-md shadow-lg py-1 max-h-60 overflow-auto focus:outline-hidden">
                    {options.map((option) => (
                        <button
                            key={option.value}
                            data-testid={option.testID}
                            onClick={() => handleSelect(option.value)}
                            className={cn(
                                "flex items-center justify-between w-full px-3 py-2 text-sm text-left hover:bg-gray-50 transition-colors",
                                currentValue === option.value && "font-medium",
                            )}
                        >
                            <span>{option.label}</span>
                            {currentValue === option.value && (
                                <Check className="h-4 w-4 text-primary" />
                            )}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

// // // //

export function TimeframeSelect(props: {
    options?: PeriodOption[];
    value?: string;
    defaultValue?: string;
    onChange?: (value: string) => void;
    className?: string;
    disabled?: boolean;
    isLoggedIn?: boolean;
    size?: "sm" | "md";
}) {
    const {
        options,
        value,
        defaultValue,
        onChange,
        className,
        disabled,
        isLoggedIn = true,
        size = "md",
    } = props;

    return (
        <PeriodSelectLoginGating isLoggedIn={isLoggedIn}>
            <PeriodSelect
                options={options}
                value={value}
                defaultValue={defaultValue}
                onChange={onChange}
                className={className}
                disabled={disabled}
                size={size}
            />
        </PeriodSelectLoginGating>
    );
}
